import type { BaseDocument } from '../mongo/base-document.interface';

/**
 * Kind of an inspection item (SWOOP-387). A `pass_fail` item is answered pass or fail; a `score` item is
 * answered with a whole number from 0 to its `maxScore`.
 */
export const INSPECTION_ITEM_KIND = { PASS_FAIL: 'pass_fail', SCORE: 'score' } as const;

export type InspectionItemKind = (typeof INSPECTION_ITEM_KIND)[keyof typeof INSPECTION_ITEM_KIND];

/**
 * How often a template's inspection is due (SWOOP-387), on demand first, then by growing interval, which is
 * the order a picker lists them. Labels belong to the UI, not to this map.
 */
export const INSPECTION_SCHEDULE = {
  ON_DEMAND: 'on_demand',
  DAILY: 'daily',
  WEEKLY: 'weekly',
  MONTHLY: 'monthly',
  EVERY_3_MONTHS: 'every_3_months',
  EVERY_6_MONTHS: 'every_6_months',
  YEARLY: 'yearly',
} as const;

export type InspectionSchedule = (typeof INSPECTION_SCHEDULE)[keyof typeof INSPECTION_SCHEDULE];

/**
 * Validation bounds shared by the API DTO and the service (SWOOP-387). `labelMax` covers a section name
 * and an item label; `maxScoreMin`..`maxScoreMax` is the whole-number range of a score item's `maxScore`.
 */
export const INSPECTION_TEMPLATE_LIMITS = {
  labelMax: 200,
  maxItemsPerSection: 100,
  maxScoreMax: 100,
  maxScoreMin: 1,
  maxSections: 30,
  nameMax: 120,
} as const;

/** One question of an inspection template (SWOOP-387). `maxScore` is set only on a `score` item. */
export interface InspectionTemplateItem {
  /** ObjectId in storage, hex on the wire; minted by the server and kept across edits. */
  id: string;
  kind: InspectionItemKind;
  label: string;
  maxScore?: number;
}

/** An ordered group of items (SWOOP-387). A section may hold no item as long as another one does. */
export interface InspectionTemplateSection {
  /** ObjectId in storage, hex on the wire; minted by the server and kept across edits. */
  id: string;
  items: InspectionTemplateItem[];
  name: string;
}

/**
 * Which vehicles a template applies to (SWOOP-387). Same field meaning as `ServicePlanApplicability`
 * (`service-plan.interface.ts:82`) without engine, energy source, transmission or year: `null` means
 * "any", a model requires a make and a trim requires a model.
 */
export interface InspectionTemplateAppliesTo {
  makeId: string | null;
  model: string | null;
  trimModelId: string | null;
}

/** What a template says, shared by the current row, its snapshots and the API shapes (SWOOP-387). */
export interface InspectionTemplateContent {
  appliesTo: InspectionTemplateAppliesTo;
  name: string;
  schedule: InspectionSchedule;
  sections: InspectionTemplateSection[];
}

/** One item as a client sends it: the server mints its id. */
export type InspectionTemplateItemInput = Omit<InspectionTemplateItem, 'id'>;

/** One section as a client sends it: the server mints its id and the ids of its items. */
export interface InspectionTemplateSectionInput {
  items: InspectionTemplateItemInput[];
  name: string;
}

/** The content of a template as a client sends it on create: no ids yet. */
export interface InspectionTemplateInput extends Omit<InspectionTemplateContent, 'sections'> {
  sections: InspectionTemplateSectionInput[];
}

/** One item as a PATCH sends it: the `id` of the stored item it keeps, or none for a new item. */
export interface InspectionTemplateItemUpdateInput extends InspectionTemplateItemInput {
  id?: string;
}

/** One section as a PATCH sends it: the `id` of the stored section it keeps, or none for a new section. */
export interface InspectionTemplateSectionUpdateInput {
  id?: string;
  items: InspectionTemplateItemUpdateInput[];
  name: string;
}

/** The content of a template as a PATCH sends it: the whole content, the `version` it loaded, ids optional. */
export interface InspectionTemplateUpdateInput extends Omit<InspectionTemplateContent, 'sections'> {
  sections: InspectionTemplateSectionUpdateInput[];
  version: number;
}

/** The API/read shape of a template. `nameKey` and tenancy fields are not exposed. Dates are ISO strings. */
export interface InspectionTemplate extends InspectionTemplateContent {
  createdAt: string;
  id: string;
  updatedAt: string;
  version: number;
}

/** The API/read shape of one immutable saved version of a template (SWOOP-387). */
export interface InspectionTemplateVersion extends InspectionTemplateContent {
  createdAt: string;
  createdBy: string;
  id: string;
  templateId: string;
  version: number;
}

/** A template with the counts the list shows (SWOOP-387). `matchCount` is today's matching vehicles. */
export interface InspectionTemplateSummary extends InspectionTemplate {
  itemCount: number;
  matchCount: number;
  sectionCount: number;
}

/**
 * The current content of a template as stored in `inspection_templates` (SWOOP-387). Archived with
 * `deletedAt` (the `hetivo-persistence` default), not a status: an archived template never needs to be
 * opened again, its snapshots carry the history.
 */
export interface InspectionTemplateRecord extends BaseDocument {
  appliesTo: InspectionTemplateAppliesTo;
  name: string;
  /** Trimmed, lowercased `name`, backing the per-tenant case-insensitive unique index. */
  nameKey: string;
  schedule: InspectionSchedule;
  sections: InspectionTemplateSection[];
  /** Integer >= 1: 1 on create, +1 on every effective save. */
  version: number;
}

export type CreateInspectionTemplateRecord = Omit<
  InspectionTemplateRecord,
  'createdAt' | 'deletedAt' | 'id' | 'tenantId' | 'updatedAt'
>;

/**
 * One immutable row per saved version, in `inspection_template_versions` (SWOOP-387): a full copy of the
 * content at that version so a later inspection can pin `{ templateId, version }`. Never updated or
 * deleted, not even when the template is archived.
 */
export interface InspectionTemplateVersionRecord extends BaseDocument {
  appliesTo: InspectionTemplateAppliesTo;
  /** The acting user, from the Mongo request context. */
  createdBy: string;
  name: string;
  schedule: InspectionSchedule;
  sections: InspectionTemplateSection[];
  templateId: string;
  version: number;
}

export type CreateInspectionTemplateVersionRecord = Omit<
  InspectionTemplateVersionRecord,
  'createdAt' | 'deletedAt' | 'id' | 'tenantId' | 'updatedAt'
>;
